import ListContainer from '@/app/components/search/components/SearchPageContainer/ListContainer';
import MapContainer from '@/app/components/search/components/SearchPageContainer/MapContainer';
import React from 'react';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import type { RouteType } from '@/app/components/search/Curiosity';
import type Form from '@/app/components/search/components/Form';
import type { DynamicFiltersAPIResponse } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import type { MapRef } from 'react-map-gl/mapbox';
import { RootListContainer, RootMapContainer } from '@/app/components/search/components/LayoutManager/styled.ts';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import FiltersManager from '@/app/components/search/components/SearchPageContainer/FiltersManager';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';

type Props = {
  routeType: RouteType;
  isWaiting: boolean;
  dynamicFiltersResponse?: DynamicFiltersAPIResponse;
  verticalFiltersRef: React.MutableRefObject<Form | undefined>;
  listContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  setCardOverHotelId: React.Dispatch<React.SetStateAction<number | undefined>>;
  layoutType: LAYOUT_TYPE;
  mapRef: React.RefObject<MapRef | null>;
  showFilters: boolean;
  cardOverHotelId: number | undefined;
  forceFullscreenMapDesktop?: boolean;
  handleShowFilters: (newShowFilters: boolean) => void;
};

const LayoutManager = ({
  listContainerRef,
  routeType,
  isWaiting,
  offset,
  dynamicFiltersResponse,
  verticalFiltersRef,
  setOffset,
  setCardOverHotelId,
  mapRef,
  showFilters,
  cardOverHotelId,
  forceFullscreenMapDesktop,
  layoutType,
  handleShowFilters,
}: Props) => {
  const { isLoading } = useSearchPageData();
  const { localization } = usePageWrapperData();
  const evolutionResultsOnlyEnabled = localization?.configValuesApp?.evolutionResultsOnlyEnabled;

  const filters = (
    <ErrorBoundary id="Results/FiltersManager">
      <FiltersManager
        isWaiting={isWaiting}
        routeType={routeType}
        handleShowFilters={handleShowFilters}
        layoutType={layoutType}
        verticalFiltersRef={verticalFiltersRef}
      />
    </ErrorBoundary>
  );

  const list = (
    <ErrorBoundary id="Results/ListContainer">
      <ListContainer
        listContainerRef={listContainerRef}
        routeType={routeType}
        offset={offset}
        dynamicFiltersResponse={dynamicFiltersResponse && dynamicFiltersResponse.filters}
        verticalFiltersRef={verticalFiltersRef}
        setCardOverHotelId={setCardOverHotelId}
        setOffset={setOffset}
      />
    </ErrorBoundary>
  );

  const map = (
    <ErrorBoundary id="Results/MapContainer">
      <MapContainer
        isWaiting={isWaiting}
        isLoading={isLoading}
        mapRef={mapRef}
        showFilters={showFilters}
        cardOverHotelId={cardOverHotelId}
        dynamicContentResponse={dynamicFiltersResponse && dynamicFiltersResponse.content}
        showCloseResize={true}
        forceFullscreenMapDesktop={forceFullscreenMapDesktop}
        shouldLaunchSearchChanged={true}
      />
    </ErrorBoundary>
  );

  if (layoutType === LAYOUT_TYPE.DESKTOP_MAP) {
    return (
      <RootMapContainer className="map-layout d-map-layout">
        {filters}
        {list}
        {map}
      </RootMapContainer>
    );
  }

  if (layoutType === LAYOUT_TYPE.DESKTOP_LIST) {
    return (
      <RootListContainer className="list-layout d-list-layout" hasSidebar={!evolutionResultsOnlyEnabled}>
        {filters}
        {list}
      </RootListContainer>
    );
  }

  if (layoutType === LAYOUT_TYPE.MOBILE_MAP) {
    return (
      <RootMapContainer className="map-layout m-map-layout">
        {filters}
        {map}
      </RootMapContainer>
    );
  }

  return (
    <RootListContainer className="list-layout m-list-layout">
      {filters}
      {list}
    </RootListContainer>
  );
};

LayoutManager.displayName = 'LayoutManager';

export default LayoutManager;
